import React, {
 createContext, useContext, useState, useCallback, ReactNode,
 useMemo
} from 'react';

interface AccessRequestsContextType {
  isModalOpen: boolean;
  selectedChannelId: string | null;
  openModal: (channelId?: string) => void;
  closeModal: () => void;
  removeRequest: string | null;
  setRemoveRequest: (removeRequest: string) => void;
}

const AccessRequestsContext = createContext<AccessRequestsContextType | undefined>(undefined);

interface AccessRequestsProviderProps {
  children: ReactNode;
}

export const AccessRequestsProvider = ({ children }: AccessRequestsProviderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [removeRequest, setRemoveRequest] = useState<string | null>(null);

  const openModal = useCallback((channelId?: string) => {
    setSelectedChannelId(channelId || null);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedChannelId(null);
  }, []);

  const contextValue = useMemo(() => ({
    isModalOpen,
    selectedChannelId,
    openModal,
    closeModal,
    removeRequest,
    setRemoveRequest,
  }), [isModalOpen, selectedChannelId, openModal, closeModal, removeRequest, setRemoveRequest]);

  return (
    <AccessRequestsContext.Provider value={contextValue}>
      {children}
    </AccessRequestsContext.Provider>
  );
};

export const useAccessRequestsContext = () => {
  const context = useContext(AccessRequestsContext);
  if (context === undefined) {
    throw new Error('useAccessRequestsContext must be used within an AccessRequestsProvider');
  }
  return context;
};
